import { FormFields, FormHeader, FormSection } from '../form';
import { AddressAutocomplete } from './AddressAutocomplete';
import { Input, ListBox } from '../../../../components';
import { useValidation } from '../../../../hooks';
import { claimFields } from '../../../../config';

import type { IListItem, IState } from '../../../../types';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useContext, useEffect } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';
import { ReportingTechContext } from '../../../../context';

interface IAddressProps {
    setSaveAndQuitChanges?: Dispatch<SetStateAction<Array<string>>>;
    states: Array<IState>;
}

export const Address: FC<IAddressProps> = ({ setSaveAndQuitChanges, states }) => {
    const { fieldRequirements } = useValidation({});
    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    const reportingTech = useContext(ReportingTechContext);

    const countryId = reportingTech.reportingTech?.clientCountryId;
    const stateProvinceLabel =
        countryId === 1
            ? localization.translate('damageLocation.stateLabel')
            : localization.translate('damageLocation.provinceLabel');
    const stateProvincePlaceholder =
        countryId === 1
            ? localization.translate('damageLocation.statePlaceholder')
            : localization.translate('damageLocation.provincePlaceholder');
    const stateProvinceTooltip =
        countryId === 1
            ? localization.translate('damageLocation.stateTooltip')
            : localization.translate('damageLocation.provinceTooltip');

    // Remove stateId 0, it is an empty placeholder.
    const filteredStates: Array<IListItem> = states
        ?.map((state) => {
            return { id: state.id, name: state.fullName ?? state.name ?? state.id, countryId: state.countryId };
        })
        .filter((state: IState) => Number(state.id) !== 0)
        .filter((state: IState) => state.countryId === countryId);

    return (
        <FormSection>
            <FormHeader>{localization.translate('damageLocation.header')}</FormHeader>

            <FormFields>
                {/* STREET ADDRESS AUTOCOMPLETE */}
                <AddressAutocomplete
                    stateList={filteredStates}
                    options={{
                        isRequired: fieldRequirements.DamageStreetAddress,
                        label: localization.translate('damageLocation.streetAddressLabel'),
                        name: claimFields.damageStreetAddress,
                        onChange: (_) => setSaveAndQuitChanges?.((prev) => [...prev, 'DamageAddress1']),
                    }}
                    tooltip={localization.translate('damageLocation.streetAddressTooltip')}
                />

                {/* CROSS STREET */}
                <Input
                    inputOptions={{
                        isRequired: fieldRequirements.DamageCrossStreet,
                        label: localization.translate('damageLocation.crossStreetLabel'),
                        name: claimFields.damageCrossStreet,
                        onChange: (_) => setSaveAndQuitChanges?.((prev) => [...prev, 'DamageCrossStreet']),
                    }}
                    tooltip={localization.translate('damageLocation.crossStreetTooltip')}
                />

                {/* CITY */}
                <Input
                    inputOptions={{
                        isRequired: fieldRequirements.DamageCity,
                        label: localization.translate('damageLocation.cityLabel'),
                        name: claimFields.damageCity,
                        onChange: (_) => setSaveAndQuitChanges?.((prev) => [...prev, 'DamageCity']),
                    }}
                    tooltip={localization.translate('damageLocation.cityTooltip')}
                />

                <div
                    className={
                        'flex w-full flex-col items-baseline space-x-0 space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0'
                    }
                >
                    {/* STATE */}
                    <div className={'w-full'}>
                        <ListBox
                            isRequired={fieldRequirements.DamageState}
                            label={stateProvinceLabel}
                            listItems={filteredStates}
                            name={claimFields.damageState}
                            onSingleChange={(_) => setSaveAndQuitChanges?.((prev) => [...prev, 'DamageState'])}
                            placeholder={stateProvincePlaceholder}
                            tooltip={stateProvinceTooltip}
                        />
                    </div>

                    {/* ZIPCODE */}
                    <div className={'mt-1 w-full'}>
                        <Input
                            inputOptions={{
                                isRequired: fieldRequirements.DamageZipCode,
                                label: localization.translate('damageLocation.zipCodeLabel'),
                                name: claimFields.damageZipCode,
                                onChange: (_) => setSaveAndQuitChanges?.((prev) => [...prev, 'DamagePostalCode']),
                            }}
                            tooltip={localization.translate('damageLocation.zipCodeTooltip')}
                            type={countryId === 1 ? 'USzip' : 'CAzip'}
                        />
                    </div>
                </div>
            </FormFields>
        </FormSection>
    );
};
